import { createSlice } from "@reduxjs/toolkit";

import { Agent } from "./agentSlice";
import { AgentEnvironment } from "./agentEnvironmentSlice";
import { AgentFunction } from "./agentFunctionSlice";

// Multi-file support types
export interface FileData {
  content: string;
  language: string;
}

export interface FilesState {
  [filename: string]: FileData;
}

// Extended AgentFunction form data with multi-file support and agent creation fields
export interface ExtendedAgentFunctionFormData extends Partial<AgentFunction> {
  files?: FilesState;
  activeFile?: string;
  agentName?: string;
  agentDescription?: string;
}

export type SessionState = {
  agentDetailsFormData: Partial<Agent>;
  agentEnvironmentsFormData: Partial<AgentEnvironment>;
  agentFunctionFormData: ExtendedAgentFunctionFormData;
  // Used to instruct the function editor to accept or reject any pending
  // Assistant-generated changes before navigation or saving.
  agentFunctionPendingAssistantAction?: "accept" | "reject" | null;
  // Monotonic token incremented whenever a pending Assistant action completes.
  agentFunctionAssistantActionToken: number;
  // Legacy names for backward compatibility
  agentFunctionPendingClaudeAction?: "accept" | "reject" | null;
  agentFunctionClaudeActionToken: number;
};

const initialState: SessionState = {
  agentDetailsFormData: {},
  agentEnvironmentsFormData: {},
  agentFunctionFormData: {},
  agentFunctionPendingAssistantAction: null,
  agentFunctionAssistantActionToken: 0,
  agentFunctionPendingClaudeAction: null,
  agentFunctionClaudeActionToken: 0,
};

const featureSlice = createSlice({
  name: "feature",
  initialState,
  reducers: {
    setAgentDetailsFormData: (state, action) => {
      state.agentDetailsFormData = action.payload;
    },
    setAgentEnvironmentsFormData: (state, action) => {
      state.agentEnvironmentsFormData = action.payload;
    },
    setAgentFunctionFormData: (state, action) => {
      state.agentFunctionFormData = action.payload;
    },
    setAgentFunctionFiles: (state, action) => {
      state.agentFunctionFormData.files = action.payload;
    },
    setAgentFunctionActiveFile: (state, action) => {
      state.agentFunctionFormData.activeFile = action.payload;
    },
    setAgentFunctionAgentName: (state, action) => {
      state.agentFunctionFormData.agentName = action.payload;
    },
    setAgentFunctionAgentDescription: (state, action) => {
      state.agentFunctionFormData.agentDescription = action.payload;
    },
    setAgentFunctionPendingClaudeAction: (state, action) => {
      state.agentFunctionPendingClaudeAction = action.payload;
    },
    bumpAgentFunctionClaudeActionToken: (state) => {
      state.agentFunctionClaudeActionToken += 1;
    },
    setAgentFunctionPendingAssistantAction: (state, action) => {
      state.agentFunctionPendingAssistantAction = action.payload;
    },
    bumpAgentFunctionAssistantActionToken: (state) => {
      state.agentFunctionAssistantActionToken += 1;
    },
  },
});

export const featureActions = featureSlice.actions;
export const featureReducer = featureSlice.reducer;
